import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { Link } from '@dndbeyond/character-common-components/es';
import {
    AbilitySummary,
    CampaignSummary,
    DarkChatBubbleSvg,
    DarkPlayButtonSvg,
    FeatureFlagContext,
    LightChatBubbleSvg,
    LightPlayButtonSvg,
    SavingThrowsSummary,
} from '@dndbeyond/character-components/es';
import {
    Ability,
    AbilityUtils,
    CampaignDataContract,
    CharacterPreferences,
    CharacterTheme,
    DataOrigin,
    DeathSaveInfo,
    DiceAdjustment,
    RuleData,
    rulesEngineSelectors,
    SenseInfo,
    SituationalSavingThrowInfoLookup,
} from '@dndbeyond/character-rules-engine/es';
import { IRollContext } from '@dndbeyond/dice';
import { GameLogNotificationWrapper } from '@dndbeyond/game-log-components';

import { sidebarActions } from '../../../../Shared/actions';
import { appEnvSelectors, characterRollContextSelectors } from '../../../../Shared/selectors';
import { GameLogState } from '../../../../Shared/stores/typings';
import { PaneComponentEnum, PaneIdentifierUtils, PaneUtils } from '../../../../Shared/utils';
import { NavigationUtils } from '../../../../Shared/utils';
import MobileDivider from '../../../components/MobileDivider';
import SavingThrowsDetails from '../../../components/SavingThrowsDetails';
import Senses from '../../../components/Senses';
import SubsectionMobile from '../../../components/SubsectionMobile';
import { SheetAppState } from '../../../typings';

interface Props extends DispatchProp {
    abilities: Array<Ability>;
    preferences: CharacterPreferences;
    savingThrowDiceAdjustments: Array<DiceAdjustment>;
    situationalBonusSavingThrowsLookup: SituationalSavingThrowInfoLookup;
    passiveInsight: number;
    passiveInvestigation: number;
    passivePerception: number;
    senses: SenseInfo;
    ruleData: RuleData;
    campaign: CampaignDataContract | null;
    theme: CharacterTheme;
    deathSaveInfo: DeathSaveInfo;
    diceEnabled: boolean;
    characterRollContext: IRollContext;
    gameLog: GameLogState;
}
class MainMobile extends React.PureComponent<Props> {
    static defaultProps = {
        diceEnabled: false,
    };

    handleSensesManageShow = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.SENSE_MANAGE));
    };

    handleSavingThrowsClick = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.SAVING_THROWS));
    };

    handleAbilitySummaryClick = (ability: Ability): void => {
        const { dispatch } = this.props;
        dispatch(
            sidebarActions.paneHistoryStart(
                PaneComponentEnum.ABILITY,
                PaneIdentifierUtils.generateAbility(AbilityUtils.getId(ability)),
            ),
        );
    };

    handleAbilityClick = (ability) => {
        const { dispatch } = this.props;
        dispatch(
            sidebarActions.paneHistoryStart(
                PaneComponentEnum.ABILITY_SAVING_THROW,
                PaneIdentifierUtils.generateAbilitySavingThrows(AbilityUtils.getId(ability)),
            ),
        );
    };

    handleDataOriginClick = (dataOrigin: DataOrigin): void => {
        const { dispatch } = this.props;

        let component = PaneUtils.getDataOriginComponentInfo(dataOrigin);
        if (component.type !== PaneComponentEnum.ERROR_404) {
            dispatch(sidebarActions.paneHistoryPush(component.type, component.identifiers));
        }
    };

    handleCampaignShow = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.CAMPAIGN));
    };

    handleGameLogClick = (evt: React.MouseEvent): void => {
        const { dispatch } = this.props;

        evt.stopPropagation();
        evt.nativeEvent.stopImmediatePropagation();

        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.GAME_LOG));
    };

    render() {
        const {
            preferences,
            abilities,
            savingThrowDiceAdjustments,
            situationalBonusSavingThrowsLookup,
            passiveInsight,
            passiveInvestigation,
            passivePerception,
            senses,
            ruleData,
            campaign,
            theme,
            diceEnabled,
            deathSaveInfo,
            gameLog,
            characterRollContext,
        } = this.props;

        return (
            <SubsectionMobile name="Main">
                <div className={`ct-main-mobile ${theme.isDarkMode ? 'ct-main-mobile--dark-mode' : ''}`}>
                    {campaign && (
                        <div
                            className={`ct-main-mobile__campaign ${
                                theme.isDarkMode ? 'ct-main-mobile__campaign--dark-mode' : ''
                            }`}
                        >
                            <CampaignSummary
                                campaign={campaign}
                                onCampaignShow={this.handleCampaignShow}
                                className={theme.isDarkMode ? 'ct-main-mobile__campaign-button--dark-mode' : ''}
                            />
                            <FeatureFlagContext.Consumer>
                                {(ffm) =>
                                    ffm.getFlag(ffm.FLAGS.RELEASE_GATE_TACTICAL_MAPS) && (
                                        <Link
                                            url={NavigationUtils.getLaunchGameUrl(campaign)}
                                            target="maps"
                                            aria-label="Launch Game"
                                        >
                                            <div className="ct-character-header__group--game-log">
                                                <div className="ct-character-header-desktop__button-icon">
                                                    {theme.isDarkMode ? <LightPlayButtonSvg /> : <DarkPlayButtonSvg />}
                                                </div>
                                            </div>
                                        </Link>
                                    )
                                }
                            </FeatureFlagContext.Consumer>
                            <GameLogNotificationWrapper
                                themeColor={theme.themeColor}
                                gameLogIsOpen={gameLog.isOpen}
                                notificationOnClick={this.handleGameLogClick}
                            >
                                <div
                                    role="button"
                                    aria-roledescription="Game Log"
                                    className="ct-character-header__group--game-log ct-character-header__group--game-log-last"
                                    onClick={this.handleGameLogClick}
                                >
                                    <div className="ct-character-header-desktop__button-icon">
                                        {theme.isDarkMode ? <LightChatBubbleSvg /> : <DarkChatBubbleSvg />}
                                    </div>
                                </div>
                            </GameLogNotificationWrapper>
                        </div>
                    )}
                    <div className="ct-main-mobile__abilities">
                        {abilities.map((ability) => (
                            <div className="ct-main-mobile__ability" key={ability.statKey}>
                                <AbilitySummary
                                    ability={ability}
                                    preferences={preferences}
                                    theme={theme}
                                    onClick={this.handleAbilitySummaryClick}
                                    diceEnabled={diceEnabled}
                                    rollContext={characterRollContext}
                                />
                            </div>
                        ))}
                    </div>
                    <MobileDivider label="Saving Throws" onClick={this.handleSavingThrowsClick} theme={theme} />
                    <div
                        className={`ct-main-mobile__saving-throws ${
                            theme.isDarkMode ? 'ct-main-mobile__saving-throws--dark-mode' : ''
                        }`}
                    >
                        <SavingThrowsSummary
                            abilities={abilities}
                            situationalBonusSavingThrowsLookup={situationalBonusSavingThrowsLookup}
                            onClick={this.handleAbilityClick}
                            diceEnabled={diceEnabled}
                            theme={theme}
                            rollContext={characterRollContext}
                        />
                        <SavingThrowsDetails
                            theme={theme}
                            abilities={abilities}
                            ruleData={ruleData}
                            savingThrowDiceAdjustments={savingThrowDiceAdjustments}
                            onDataOriginClick={this.handleDataOriginClick}
                            deathSaveInfo={deathSaveInfo}
                        />
                    </div>
                    <MobileDivider isEnd={true} theme={theme} />
                    <MobileDivider label="Senses" onClick={this.handleSensesManageShow} theme={theme} />
                    <Senses
                        senses={senses}
                        theme={theme}
                        passiveInsight={passiveInsight}
                        passiveInvestigation={passiveInvestigation}
                        passivePerception={passivePerception}
                        onClick={this.handleSensesManageShow}
                    />
                    <MobileDivider isEnd={true} theme={theme} />
                </div>
            </SubsectionMobile>
        );
    }
}

function mapStateToProps(state: SheetAppState) {
    return {
        abilities: rulesEngineSelectors.getAbilities(state),
        preferences: rulesEngineSelectors.getCharacterPreferences(state),
        savingThrowDiceAdjustments: rulesEngineSelectors.getSavingThrowDiceAdjustments(state),
        situationalBonusSavingThrowsLookup: rulesEngineSelectors.getSituationalBonusSavingThrowsLookup(state),
        passivePerception: rulesEngineSelectors.getPassivePerception(state),
        passiveInvestigation: rulesEngineSelectors.getPassiveInvestigation(state),
        passiveInsight: rulesEngineSelectors.getPassiveInsight(state),
        senses: rulesEngineSelectors.getSenseInfo(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        campaign: rulesEngineSelectors.getCampaign(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
        deathSaveInfo: rulesEngineSelectors.getDeathSaveInfo(state),
        diceEnabled: appEnvSelectors.getDiceEnabled(state),
        characterRollContext: characterRollContextSelectors.getCharacterRollContext(state),
        gameLog: appEnvSelectors.getGameLog(state),
    };
}

export default connect(mapStateToProps)(MainMobile);
